import type { TDocumentDefinitions, Content, TableCell } from 'pdfmake/interfaces';
import {
  mm, fmtPrice, fmtDate, interpolate,
  companyLines, companySocialsLine, partyLines, totalsRows, buildVars,
  computeVatBreakdown,
  COLUMN_LABELS, COLUMN_WIDTHS, linesCell,
  type DesignCtx,
} from './design-utils';
import { amountInWordsFr } from '../amount-in-words';

// "Modern" — colored band header across the top with the title on the right
// and the company name reversed out in white. Logo optional inside the band.
export function buildModernDoc(ctx: DesignCtx): TDocumentDefinitions {
  const { options: o, data, company, docTypeTitle } = ctx;
  const vars = buildVars(ctx);
  const title = o.customTitle || docTypeTitle;

  // A colored rectangle on page 1 only — drawn via background fn so it's
  // anchored in page coordinates, not in flow.
  const bandHeightMm = 36;

  const header: TableCell[] = o.linesColumns.map((c) => ({
    text: COLUMN_LABELS[c], bold: true, fontSize: 9, color: '#fff',
    alignment: c === 'description' ? 'left' : c === 'number' ? 'center' : 'right',
  }));
  const rows: TableCell[][] = data.lines.map((l) => o.linesColumns.map((c) => linesCell(c, l)));

  const partyLabel = data.supplier ? 'FOURNISSEUR' : 'CLIENT';
  const partyTxt = partyLines(data.supplier ?? data.client, o);

  const infoRow: Content = {
    margin: [0, 10, 0, 15],
    columns: [
      {
        width: '*',
        stack: [
          { text: partyLabel, fontSize: 9, bold: true, color: o.primaryColor, margin: [0, 0, 0, 3] },
          ...partyTxt.map((line, i) => ({ text: line, fontSize: i === 0 ? 11 : 9, bold: i === 0 })),
        ],
      },
      {
        width: 'auto',
        stack: [
          ...(o.showNumber ? [{ text: `N° ${data.number}`, fontSize: 10, bold: true } as Content] : []),
          ...(o.showDate ? [{ text: `Date : ${fmtDate(data.docDate)}`, fontSize: 9, color: '#666' } as Content] : []),
          ...(o.showValidityDate && data.validityDate ? [{ text: `Validité : ${fmtDate(data.validityDate)}`, fontSize: 9, color: '#666' } as Content] : []),
        ],
        alignment: 'right',
      },
    ],
  };

  const linesTable: Content = {
    margin: [0, 0, 0, 15],
    table: {
      headerRows: 1,
      widths: o.linesColumns.map((c) => COLUMN_WIDTHS[c]),
      body: [header, ...rows],
    },
    layout: {
      hLineWidth: () => 0,
      vLineWidth: () => 0,
      paddingLeft: () => 8, paddingRight: () => 8, paddingTop: () => 6, paddingBottom: () => 6,
      fillColor: (i: number) => i === 0 ? o.primaryColor : (o.zebraRows && i % 2 === 0) ? '#F5F7FA' : null,
    },
  };

  // VAT breakdown (grouped by rate)
  const vatRows = o.showVatBreakdown ? computeVatBreakdown(data.lines) : [];
  const vatHeader: TableCell[] = [
    { text: 'Base TVA', fontSize: 9, bold: true, color: '#fff', fillColor: o.primaryColor, alignment: 'left' },
    { text: 'Taux TVA', fontSize: 9, bold: true, color: '#fff', fillColor: o.primaryColor, alignment: 'center' },
    { text: 'TVA', fontSize: 9, bold: true, color: '#fff', fillColor: o.primaryColor, alignment: 'right' },
  ];
  const vatBodyRows: TableCell[][] = vatRows.map((r) => [
    { text: fmtPrice(r.base), alignment: 'left', fontSize: 9 },
    { text: `${r.rate}%`, alignment: 'center', fontSize: 9 },
    { text: fmtPrice(r.tax), alignment: 'right', fontSize: 9 },
  ]);
  const vatBreakdownTable: Content = (o.showVatBreakdown && vatRows.length > 0) ? {
    table: { widths: ['*', 'auto', 'auto'], body: [vatHeader, ...vatBodyRows] },
    layout: {
      hLineWidth: () => 0, vLineWidth: () => 0,
      paddingLeft: () => 6, paddingRight: () => 6, paddingTop: () => 4, paddingBottom: () => 4,
      fillColor: (i: number) => (i > 0 && i % 2 === 0) ? '#F5F7FA' : null,
    },
  } : { text: '' };

  const totals = totalsRows(ctx);
  const totalsBodyTable: Content = totals.length ? {
    table: { widths: ['*', 'auto'], body: totals },
    layout: {
      hLineWidth: () => 0, vLineWidth: () => 0,
      paddingTop: () => 5, paddingBottom: () => 5, paddingLeft: () => 8, paddingRight: () => 8,
      fillColor: (i: number) => i % 2 === 0 ? '#F5F7FA' : null,
    },
  } : { text: '' };

  const totalsTable: Content = (totals.length || vatRows.length) ? {
    margin: [0, 0, 0, 15],
    columns: [
      vatRows.length > 0 ? { width: mm(85), stack: [vatBreakdownTable] } : { width: '*', text: '' },
      { width: 10, text: '' },
      totals.length > 0 ? { width: mm(85), stack: [totalsBodyTable] } : { width: '*', text: '' },
    ],
  } : { text: '' };

  const amountLabel = (() => {
    const docLabel = (o.customTitle || docTypeTitle).toLowerCase();
    if (docLabel.includes('devis')) return 'Arrêter le Présent Devis à la Somme de :';
    if (docLabel.includes('livraison')) return 'Arrêter le Présent BL à la Somme de :';
    if (docLabel.includes('avoir')) return 'Arrêter le Présent Avoir à la Somme de :';
    if (docLabel.includes('réception') || docLabel.includes('reception')) return 'Arrêter le Présent BR à la Somme de :';
    if (docLabel.includes('commande')) return 'Arrêter la Présente Commande à la Somme de :';
    return 'Arrêter la Présente Facture à la Somme de :';
  })();
  const amountInWordsBlock: Content = o.showAmountInWords ? {
    stack: [
      { text: amountLabel, fontSize: 9, color: '#666', margin: [0, 0, 0, 2] },
      { text: amountInWordsFr(data.totalTtc), fontSize: 9, bold: true, color: o.primaryColor },
    ],
    margin: [0, 0, 0, 15],
  } : { text: '' };

  const notesBlock: Content = (o.showNotes && data.notes) ? {
    stack: [
      { text: 'NOTES', fontSize: 9, bold: true, color: o.primaryColor, margin: [0, 0, 0, 4] },
      { text: data.notes, fontSize: 9, color: '#333' },
    ],
    margin: [0, 0, 0, 15],
  } : { text: '' };

  return {
    pageSize: 'A4',
    pageMargins: [mm(15), mm(bandHeightMm + 15), mm(15), mm(20)],
    background: (currentPage: number) => {
      if (currentPage !== 1) return [];
      const content: Content[] = [
        { canvas: [{ type: 'rect', x: 0, y: 0, w: mm(210), h: mm(bandHeightMm), color: o.primaryColor }] },
      ];
      // Company name + body lines + socials reversed out on the band
      content.push({
        absolutePosition: { x: mm(15), y: mm(8) },
        text: o.showCompanyName ? company.companyName : '',
        fontSize: 16, bold: true, color: '#fff',
      });
      const bodyLines = companyLines(company, o);
      if (bodyLines.length > 0) {
        content.push({
          absolutePosition: { x: mm(15), y: mm(17) },
          text: bodyLines.join(' · '),
          fontSize: 8, color: '#fff',
        });
      }
      const socials = companySocialsLine(company, o);
      if (socials) {
        content.push({
          absolutePosition: { x: mm(15), y: mm(bandHeightMm - 6) },
          text: socials,
          fontSize: 8, color: '#fff',
        });
      }
      if (o.showTitle) {
        content.push({
          absolutePosition: { x: mm(110), y: mm(10) },
          text: title,
          fontSize: 20, bold: true, color: '#fff', alignment: 'right',
        });
      }
      if (o.showNumber) {
        content.push({
          absolutePosition: { x: mm(110), y: mm(20) },
          text: data.number,
          fontSize: 11, color: '#fff', alignment: 'right',
        });
      }
      // Logo on top-right if present
      if (o.logoPosition !== 'hidden' && ctx.logoDataUrl) {
        content.push({
          absolutePosition: { x: mm(210 - 15 - o.logoSizeMm), y: mm((bandHeightMm - o.logoSizeMm) / 2) },
          image: ctx.logoDataUrl,
          width: mm(o.logoSizeMm), height: mm(o.logoSizeMm), fit: [mm(o.logoSizeMm), mm(o.logoSizeMm)],
        });
      }
      return content;
    },
    content: [
      infoRow,
      linesTable,
      totalsTable,
      amountInWordsBlock,
      notesBlock,
    ],
    footer: o.showFooter ? ((currentPage: number, pageCount: number): Content => ({
      margin: [mm(15), 6, mm(15), 0],
      columns: [
        { text: interpolate(o.footerText, vars), fontSize: 8, color: '#999', alignment: o.showPageNumbers ? 'left' : 'center' },
        ...(o.showPageNumbers ? [{ text: `${currentPage} / ${pageCount}`, fontSize: 8, color: '#999', alignment: 'right' as const }] : []),
      ],
    })) : undefined,
    defaultStyle: { fontSize: 9 },
  };
}
